import qs from 'qs'
import { assocPath,dissoc, dissocPath } from 'ramda'

import type { FilterValues } from 'modules/products'
import { isNilOrEmpty,isNotNilOrEmpty } from 'shared/utils/ramda'

export interface PriceType {
  min: string
  max: string
}

export enum FILTER_NAMES {
  Type = 'Typ produktu',
  Price = 'Cena',
  Producer = 'Producent',
  Availability = 'Dostępność w dostawie'
}

export const PRICE_FILTER_OPTIONS = [
  {
    label: 'Dowolna kwota',
    min: '',
    max: ''
  },
  {
    label: 'poniżej 10zł',
    min: '',
    max: '10'
  },
  {
    label: 'od 10zł do 20zł',
    min: '10',
    max: '20'
  },
  {
    label: 'od 20zł do 50zł',
    min: '20',
    max: '50'
  },
  {
    label: 'powyżej 50zł',
    min: '50',
    max: ''
  }
] as const

export const parsePriceString = (priceString: string) => {
  const [min, max] = priceString.split('|').map(item => Number(item.trim()))

  return {
    min: min ? String(min / 100) : '',
    max: max ? String(max / 100) : ''
  }
}

type PriceFilterKey = 'min' | 'max'

export const convertPriceInputToQueryFilters = (price: number | string) => {
  const priceNumber = Number(price)

  if (priceNumber === 0) {
    return ''
  } else {
    return (priceNumber * 100).toFixed(0)
  }
}

export type MapPriceFiltersToQueryPayload = Record<
  PriceFilterKey,
  string | number
>

const getPriceParam = (price: PriceType) => {
  const min = String(price.min)
  const max = String(price.max)

  let priceParam = ''

  if (isNotNilOrEmpty(min)) {
    priceParam = min
  }

  if (isNotNilOrEmpty(max)) {
    if (isNotNilOrEmpty(min)) {
      priceParam += `|${max}`
    } else {
      priceParam += `0|${max}`
    }
  }

  return priceParam.length > 0 ? priceParam : null
}

export const mapPriceFiltersToQuery = (
  parameters: MapPriceFiltersToQueryPayload
) => {
  const output: Record<PriceFilterKey, string> = {
    min: '',
    max: ''
  }

  for (const k in parameters) {
    const key = k as PriceFilterKey

    output[key] = convertPriceInputToQueryFilters(parameters[key])
  }

  return getPriceParam(output)
}

export class PriceFilterValidator {
  static validate({ min, max }: PriceType) {
    if (!isNotNilOrEmpty(min) || !isNotNilOrEmpty(max)) return true

    return Number(min) < Number(max)
  }
  static isNoSelected(prices: PriceType) {
    return prices.min === '' && prices.max === ''
  }

  static isUnder10(prices: PriceType) {
    const priceMin = Number(prices.min)
    const priceMax = Number(prices.max)

    return priceMin === 0 && priceMax === 10
  }

  static isBetween10_20(prices: PriceType) {
    const priceMin = Number(prices.min)
    const priceMax = Number(prices.max)

    return priceMin === 10 && priceMax === 20
  }

  static isBetween20_50(prices: PriceType) {
    const priceMin = Number(prices.min)
    const priceMax = Number(prices.max)

    return priceMin === 20 && priceMax === 50
  }

  static isAbove50(prices: PriceType) {
    const priceMin = Number(prices.min)

    return priceMin === 50 && prices.max === ''
  }
}

export const createQueryString = (
  searchParams: URLSearchParams,
  key: string,
  value: string | string[] | null
) => {
  const params = qs.parse(searchParams.toString())

  let newParams = { ...params }

  if (isNilOrEmpty(value)) {
    newParams = dissocPath(['filter', key], newParams)
    // if filter object is empty, remove it
    if (newParams.filter && Object.keys(newParams.filter).length === 0) {
      newParams = dissoc('filter', newParams)
    }
  } else {
    newParams = assocPath(['filter', key], value, newParams)
  }

  return qs.stringify(newParams)
}

export const clearQueryParams = (query: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(query).filter(
      ([key]) => key !== 'entity' && !key.startsWith('filter')
    )
  )

export const haveFiltersChanged = (
  newFilters: FilterValues,
  currentFilters: FilterValues
) =>
  !Object.entries(newFilters).every(([key, value]) => {
    const current = currentFilters[key as keyof FilterValues]

    // Condition for null/undefined: intentional loose equality for treating null/undefined values as equivalent
    if (value == null && current == null) return true

    // Condition for arrays
    if (Array.isArray(value) && Array.isArray(current)) {
      if (value.length !== current.length) return false

      return value.every(v => current.includes(v))
    }

    // Condition for scalars
    return value === current
  })
